/**
 * YmmSelector Styles
 * ==================
 *
 * Styles for the Year/Make/Model vehicle selector component on the homepage.
 * This component allows users to select vehicles from two year ranges:
 * - Classic vehicles (1947-2017)
 * - Modern vehicles (2018-Present)
 *
 * Component Hierarchy:
 * └── Container (main wrapper)
 *     ├── Label (header text)
 *     ├── SectionSwitcher (Automotive/Powersports links)
 *     ├── TabsContainer
 *     │   └── StyledTabs
 *     │       └── StyledTab (x2)
 *     └── Content OR ModernVehicleWrapper
 *         └── Filters
 *             └── Filter (x4: Make, Model, Trim, Year)
 *
 * Breakpoints Reference:
 * - mobile: < 576px
 * - tablet: < 820px
 * - laptop: >= 820px
 * - desktop: >= 1440px
 * - maxWidth: >= 1920px
 */

import styled, { css } from "styled-components"
import { up, down } from "styled-breakpoints"
import colors from "@constants/colors"
import { columns14, columnsInMaxWidth } from "@constants/layout"
import { Tabs, Tab } from "@mui/material"

// ════════════════════════════════════════════════════════════════════════════
// SECTION 1: MAIN CONTAINER
// ════════════════════════════════════════════════════════════════════════════

/**
 * Container
 * ---------
 * Main wrapper for the entire YMM selector component.
 *
 * @prop {string} bgColor - Background color (defaults to black)
 *
 * Features:
 * - Dark background with gradient on mobile
 * - Responsive horizontal padding using grid columns
 * - Global button styling for the "Build" button
 */
export const Container = styled.div<{
  bgColor: string
}>`
  ${({ bgColor }) => css`
    /* === Base Styles === */
    display: block;
    font-family: "Jost", sans-serif;
    background-color: ${bgColor ? bgColor : `#000000`};

    /* === Tablet & Mobile === */
    ${down("tablet")} {
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0) 100%
      );
      padding: 30px 0;
    }

    /* === Laptop === */
    ${up("laptop")} {
      padding: 32px ${columns14(1)};
    }

    /* === Max Width (1920px+) === */
    ${up("maxWidth")} {
      padding: 32px ${columnsInMaxWidth(1)};
    }
  `}

  /* === Build Button (Classic Tab) === */
  button {
    background: ${colors.red};
    color: #fff;
    min-width: 150px;
    text-transform: uppercase;
    border: 0;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: 0.6s ease-out;

    ${down("tablet")} {
      width: 100%;
      padding: 15px;
    }

    &:hover {
      color: ${colors.red};
      background: #fff;
    }

    &:disabled {
      background: ${colors.mediumGray};
      cursor: not-allowed;
    }
  }
`

// ════════════════════════════════════════════════════════════════════════════
// SECTION 2: TAB CONTENT WRAPPERS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Content
 * -------
 * Wrapper for the CLASSIC vehicle selector (1947-2017 tab).
 * Contains the Make/Model/Trim/Year dropdowns and Build button.
 *
 * Layout:
 * - Desktop: Horizontal row
 * - Mobile: Vertical stack
 */
export const Content = styled.div`
  /* === Base Styles === */
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 16px;

  /* === Laptop+ === */
  ${up("laptop")} {
    flex-direction: row;
    padding: 0;
  }

  /* === Tablet & Mobile === */
  ${down("tablet")} {
    padding: 10px 40px;
  }
`

/**
 * ModernVehicleWrapper
 * --------------------
 * Wrapper for the MODERN vehicle selector (2018-Present tab).
 * Wraps the ModernVehicleSelector component and provides style overrides
 * to match the classic tab's appearance.
 *
 * Note: ModernVehicleSelector is also used in the navbar car selector,
 * so we use this wrapper to apply YmmSelector-specific overrides without
 * affecting the navbar version.
 */
export const ModernVehicleWrapper = styled.div`
  /* === Base Styles === */
  width: fit-content;
  padding: 16px;

  /* === Laptop+ === */
  ${up("laptop")} {
    padding: 0;
  }

  /* === Tablet & Mobile === */
  ${down("tablet")} {
    width: 100%; /* Full width for proper mobile layout */
    padding: 10px 40px;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * CHILD OVERRIDES: SelectInput
   * Override select input widths to match classic tab (150px on desktop)
   * ───────────────────────────────────────────────────────────────────────── */
  [class*="SelectInput__Container"] {
    width: 150px;

    ${down("tablet")} {
      width: 100%;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * CHILD OVERRIDES: SectionContainer (from VehicleSection)
   * Remove the default margin-bottom: 48px for tighter spacing
   * ───────────────────────────────────────────────────────────────────────── */
  > div {
    margin-bottom: 0;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * CHILD OVERRIDES: Filters (from VehicleSection)
   * Remove the default margin-bottom: 24px since we're not showing
   * the "Popular Builds" section (showPopularBuilds={false})
   * ───────────────────────────────────────────────────────────────────────── */
  [class*="Filters"] {
    margin-bottom: 0;
  }
`

// ════════════════════════════════════════════════════════════════════════════
// SECTION 3: FILTER COMPONENTS (Classic Tab Dropdowns)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Filters
 * -------
 * Container for the filter dropdowns row (Make, Model, Trim, Year).
 * Used in the CLASSIC tab only.
 *
 * Layout:
 * - Desktop: Horizontal row with flex
 * - Mobile: Vertical stack (block display)
 */
export const Filters = styled.div`
  /* === Base Styles === */
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  /* === Laptop+ === */
  ${up("tablet")} {
    flex-direction: row;
    padding: 0;
  }

  /* === Tablet & Mobile === */
  ${down("tablet")} {
    display: block;
    width: 100%;
  }
`

/**
 * Filter
 * ------
 * Individual filter dropdown wrapper (Make, Model, Trim, Year).
 * Used in the CLASSIC tab only.
 *
 * @prop {boolean} disabled - Whether the filter is disabled (grayed out)
 *
 * Features:
 * - Disabled state styling (gray background, no pointer events)
 * - MUI Input customizations for consistent appearance
 */
export const Filter = styled.div<{ disabled?: boolean }>`
  /* === Base Styles === */
  min-width: 150px;
  font-family: "Jost", sans-serif;
  flex-grow: 0;
  margin-right: 20px;

  /* === Tablet & Mobile === */
  ${down("tablet")} {
    margin-right: 0;
    margin-bottom: 10px;
  }

  /* === Disabled State === */
  ${({ disabled }) => css`
    pointer-events: ${disabled ? "none" : "all"};

    [class*="SelectInput__Container"] {
      background-color: ${disabled ? colors.mediumGray : colors.lightestGray};
    }
  `}

  /* ─────────────────────────────────────────────────────────────────────────
   * MUI OVERRIDES: SelectInput Container
   * ───────────────────────────────────────────────────────────────────────── */
  [class*="SelectInput__Container"] {
    padding: 0 8px;
    height: 36px;

    ${down("tablet")} {
      height: 50px;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * MUI OVERRIDES: Input Form Control
   * ───────────────────────────────────────────────────────────────────────── */
  [class*="MuiInput-formControl"] {
    margin-top: 16px;
    height: 16px;
    font-size: 14px;

    ${down("tablet")} {
      margin-top: 22px;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * MUI OVERRIDES: Input Label
   * ───────────────────────────────────────────────────────────────────────── */
  [class*="MuiInputLabel-root"] {
    font-family: "Jost", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    line-height: 16px;
    transform: translate(0, 10px) scale(1);

    ${down("tablet")} {
      transform: translate(0, 18px) scale(1);
    }
  }

  .MuiInputLabel-shrink {
    transform: translate(0, 4px) scale(0.6) !important;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * MUI OVERRIDES: Select Icon (dropdown arrow)
   * ───────────────────────────────────────────────────────────────────────── */
  .MuiNativeSelect-icon {
    top: 0px;
  }
`

// ════════════════════════════════════════════════════════════════════════════
// SECTION 4: HEADER & NAVIGATION
// ════════════════════════════════════════════════════════════════════════════

/**
 * Label
 * -----
 * Header section displaying "Shop by vehicle" and "Let's start that project".
 *
 * @prop {string} fontColor - Text color (defaults to white)
 *
 * Child Elements:
 * - div: Subtitle ("Shop by vehicle")
 * - h1: Main title ("Let's start that project") - hidden on mobile
 */
export const Label = styled.div<{
  fontColor: string
}>`
  ${({ fontColor }) => css`
    /* === Base Styles === */
    color: ${fontColor ? fontColor : `#ffffff`};
    margin-bottom: 10px;
    text-transform: uppercase;
    font-family: "Jost", sans-serif;

    /* === Tablet & Mobile === */
    ${down("tablet")} {
      margin-bottom: 0;
      text-shadow: 0 0 10px black;
    }

    /* === Subtitle === */
    > div {
      font-size: 15px;
      font-weight: 200;

      ${down("tablet")} {
        text-align: center;
        font-size: 20px;
        font-weight: 600;
      }
    }

    /* === Main Title (hidden on mobile) === */
    > h1 {
      font-size: 25px;
      line-height: 25px;
      font-weight: 400;
      margin: 0;

      ${down("tablet")} {
        display: none;
      }
    }
  `}
`

/**
 * SectionSwitcher
 * ---------------
 * Navigation links for switching between Automotive and Powersports sections.
 *
 * @prop {string} fontColor - Text color (defaults to white)
 *
 * Features:
 * - Active state with underline
 * - Centered on mobile
 */
export const SectionSwitcher = styled.div<{
  fontColor: string
}>`
  ${({ fontColor }) => css`
    /* === Base Styles === */
    color: ${fontColor ? fontColor : `#ffffff`};
    margin-bottom: 10px;
    text-transform: uppercase;
    font-family: "Jost", sans-serif;
    display: flex;
    gap: 10px;

    /* === Tablet & Mobile === */
    ${down("tablet")} {
      margin-bottom: 0;
      text-shadow: 0 0 10px black;
      align-items: center;
      justify-content: center;
    }

    /* === Navigation Links === */
    a {
      font-size: 15px;
      margin-right: 30px;

      .mobile-label {
        display: none;
      }

      /* Active State */
      &.active {
        border-bottom: 2px solid ${fontColor ? fontColor : `#ffffff`};
      }

      &:last-child {
        margin-right: 0px;
      }

      ${down("tablet")} {
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        margin-right: 0px;

        .desktop-label {
          display: none;
        }

        .mobile-label {
          display: inline;
        }
      }
    }
  `}
`

// ════════════════════════════════════════════════════════════════════════════
// SECTION 5: YEAR RANGE TABS (1947-2017 / 2018-Present)
// ════════════════════════════════════════════════════════════════════════════

/**
 * TabsContainer
 * -------------
 * Wrapper for the year range tab buttons.
 * Matches the styling from MenuDesktop/CarSelector.
 *
 * Features:
 * - Horizontal padding on mobile to align with content below
 * - Top margin on mobile for spacing from header
 */
export const TabsContainer = styled.div`
  /* === Base Styles === */
  /* margin-bottom: 32px; */
  width: fit-content; /* Fit to content width */

  /* === Tablet & Mobile === */
  ${down("tablet")} {
    margin-bottom: 0;
    margin-top: 16px;
    padding: 0; /* Align with Content/ModernVehicleWrapper padding */
    width: 100%; /* Full width on mobile */
  }
`

/**
 * StyledTabsWithBorder
 * --------------------
 * Custom styled Tabs component with transparent background for YmmSelector.
 * Based on TabsWithBorder but customized for homepage use.
 *
 * Features:
 * - Transparent background
 * - Red border line at bottom
 * - No default indicator
 */
export const StyledTabsWithBorder = styled(Tabs)`
  && {
    min-height: 0;
    position: relative;
    width: 100%; /* Full width of TabsContainer */
    margin-bottom: 20px;
  }

  &&::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${colors.red};
    z-index: 0;
  }

  &.MuiTabs-root {
    min-height: 0;
  }

  .MuiTabs-indicator {
    display: none;
  }

  .MuiTabs-flexContainer {
    gap: 0;
    flex-wrap: nowrap;
    position: relative;
    z-index: 1;
    width: 100%; /* Ensure flexContainer takes full width */

    ${down("mobile")} {
      width: 100%;
    }
  }
`

/**
 * StyledTabWithBorder
 * -------------------
 * Custom styled Tab component with semi-transparent black background for YmmSelector.
 * Based on TabWithBorder but customized for homepage use.
 *
 * Features:
 * - Semi-transparent black background (rgba(0, 0, 0, 0.3))
 * - White/light text color for dark backgrounds
 * - Red border when selected
 * - Rounded top corners
 */
export const StyledTabWithBorder = styled(Tab)`
  && {
    font-family: "Jost", sans-serif;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
    padding: 8px 16px;
    margin-right: 2px;
    min-height: auto;
    min-width: auto;
    text-align: left;
    align-items: flex-start;
    opacity: 1;
    position: relative;
    z-index: 1;
    white-space: nowrap;
    flex-shrink: 0;
    letter-spacing: 0;
    background: transparent;
    border-radius: 3px 3px 0 0;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-bottom: none;
    margin-bottom: 1px;
    transition: all 0.2s;
  }

  ${down("tablet")} {
    && {
      font-size: 16px;
      margin-right: 2px;
      padding: 8px 16px;
      width: auto;
      flex: 0 0 auto;
      min-width: auto;
      max-width: none;
    }
  }

  ${down("mobile")} {
    && {
      font-size: 14px;
      margin-right: 2px;
      padding: 6px 12px;
      width: auto !important;
      flex: 0 0 auto !important;
      min-width: auto !important;
      max-width: none !important;
    }
  }

  &&.Mui-selected {
    color: #ffffff;
    background: ${colors.red};
    border: 1px solid ${colors.red};
    border-bottom: none;
    margin-bottom: -1px; /* Overlap the red border line */
    z-index: 2;
    /* Use box-shadow to cover the bottom border line */
    box-shadow: 0 1px 0 0 ${colors.red};
  }

  &&:hover:not(.Mui-selected) {
    color: ${colors.red};
    background: transparent;
  }

  .MuiTab-wrapper {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }

  span {
    white-space: nowrap;
    display: inline;
    font-weight: 500;
  }
`
